<template>
	<div class="reports-view">
		<DashboardHeader
			:title="$route.name"
			:buttonItems="buttonItems"
			@click="handleHeaderClick"
		/>
		<div class="container-fluid mt--6">
			<b-overlay :show="isLoading" rounded="sm" no-wrap />

			<!-- Filters -->
			<Card>
				<b-row slot="header" align-v="center">
					<b-col cols="8">
						<h3 class="mb-0">Filters</h3>
					</b-col>
					<b-col cols="4" class="text-right">
						<BaseButton size="sm" type="primary" @click="clearFilters">Reset filters</BaseButton>
					</b-col>
				</b-row>
				<b-row align-v="end">
					<b-col md="4" sm="6" class="mb-3">
						<label class="form-control-label">From</label>
						<b-form-input v-model="fromDate" type="date" size="sm" />
					</b-col>
					<b-col md="4" sm="6" class="mb-3">
						<label class="form-control-label">To</label>
						<b-form-input v-model="toDate" type="date" size="sm" />
					</b-col>
					<b-col md="4" sm="6" class="mb-3" v-if="ds.hasDataSets">
						<label class="form-control-label">Event</label>
						<b-form-select v-model="dataSetFilter" :options="dataSetOptions" size="sm" />
					</b-col>
				</b-row>
				<div slot="footer" class="report-footer">
					{{ filteredOrders.length }} orders in range. Totals count paid orders only.
					<span v-if="loadedAt" class="float-right">Data loaded {{ loadedAt.toLocaleTimeString() }}</span>
					<div>For payouts, refunds and full transaction detail, use your Stripe dashboard.</div>
				</div>
			</Card>

			<!-- 1. Summary KPIs -->
			<b-row>
				<b-col xl="3" md="6" v-for="kpi in kpis" :key="kpi.title">
					<StatsCard :type="kpi.type" :icon="kpi.icon" class="report-kpi">
						<h5 class="card-title text-uppercase text-muted mb-1">{{ kpi.title }}</h5>
						<span class="h1 font-weight-bold mb-0 report-kpi-value">{{ kpi.value }}</span>
						<template slot="footer">
							<span class="text-muted report-kpi-note">{{ kpi.footer }}</span>
						</template>
					</StatsCard>
				</b-col>
			</b-row>

			<!-- 3. Registrations over time -->
			<Card no-body>
				<b-row slot="header" align-v="center">
					<b-col md="6">
						<h3 class="mb-0">Registrations Over Time</h3>
					</b-col>
					<b-col md="6" class="text-right">
						<b-form-radio-group
							v-model="granularity"
							:options="granularityOptions"
							buttons
							button-variant="outline-primary"
							size="sm"
							class="mr-2"
						/>
						<BaseButton size="sm" type="primary" @click="exportOverTime">
							<i class="fas fa-download"></i> CSV
						</BaseButton>
					</b-col>
				</b-row>
				<div class="p-3" v-if="overTime.length">
					<BarChart
						:key="chartKey"
						:chart-data="overTimeChartData"
						:extra-options="overTimeChartOptions"
						:styles="{ height: '280px', position: 'relative' }"
					/>
				</div>
				<p v-else class="text-muted text-center my-4">No paid registrations in this range.</p>
				<b-table
					v-if="overTime.length"
					:items="overTimeTableRows"
					:fields="overTimeFields"
					small
					hover
					responsive
					sticky-header="260px"
					class="mb-0"
				>
					<template #cell(collectedCents)="row">{{ money(row.value) }}</template>
				</b-table>
			</Card>

			<!-- 2. Registrations & revenue by product -->
			<Card no-body>
				<b-row slot="header" align-v="center">
					<b-col cols="8">
						<h3 class="mb-0">Registrations &amp; Revenue by Product</h3>
					</b-col>
					<b-col cols="4" class="text-right">
						<BaseButton size="sm" type="primary" @click="exportProducts">
							<i class="fas fa-download"></i> CSV
						</BaseButton>
					</b-col>
				</b-row>
				<b-table
					:items="products"
					:fields="productFields"
					show-empty
					empty-text="No product sales in this range."
					small
					hover
					responsive
					foot-clone
					no-footer-sorting
					class="mb-0"
				>
					<template #cell(type)="row">
						<b-badge :variant="row.value === 'Exhibitor' ? 'primary' : 'success'">{{ row.value }}</b-badge>
					</template>
					<template #cell(grossCents)="row">{{ money(row.value) }}</template>
					<template #foot(type)>Total</template>
					<template #foot(event)><span></span></template>
					<template #foot(productTitle)><span></span></template>
					<template #foot(productType)><span></span></template>
					<template #foot(units)>{{ sum(products, 'units') }}</template>
					<template #foot(orders)><span></span></template>
					<template #foot(grossCents)>{{ money(sum(products, 'grossCents')) }}</template>
				</b-table>
				<div slot="footer" class="report-footer">
					Gross is the list price before coupons and fees.
				</div>
			</Card>

			<!-- 4. Coupon usage -->
			<Card no-body>
				<b-row slot="header" align-v="center">
					<b-col cols="8">
						<h3 class="mb-0">Coupon Usage</h3>
					</b-col>
					<b-col cols="4" class="text-right">
						<BaseButton size="sm" type="primary" @click="exportCoupons">
							<i class="fas fa-download"></i> CSV
						</BaseButton>
					</b-col>
				</b-row>
				<b-table
					:items="coupons"
					:fields="couponFields"
					show-empty
					empty-text="No coupons redeemed in this range."
					small
					hover
					responsive
					class="mb-0"
				>
					<template #cell(code)="row"><code>{{ row.value }}</code></template>
					<template #cell(status)="row">
						<b-badge :variant="row.value === 'Active' ? 'success' : 'secondary'">{{ row.value }}</b-badge>
					</template>
					<template #cell(discountCents)="row">{{ money(row.value) }}</template>
					<template #cell(collectedCents)="row">{{ money(row.value) }}</template>
				</b-table>
			</Card>

			<!-- 5. Exhibitors -->
			<Card no-body>
				<b-row slot="header" align-v="center">
					<b-col cols="8">
						<h3 class="mb-0">Exhibitors</h3>
					</b-col>
					<b-col cols="4" class="text-right">
						<BaseButton size="sm" type="primary" @click="exportExhibitors">
							<i class="fas fa-download"></i> CSV
						</BaseButton>
					</b-col>
				</b-row>
				<b-table
					:items="exhibitors"
					:fields="exhibitorFields"
					:per-page="perPage"
					:current-page="exhibitorPage"
					show-empty
					empty-text="No exhibitor registrations in this range."
					small
					hover
					responsive
					tbody-tr-class="cursor-pointer"
					@row-clicked="openOrder"
					class="mb-0"
				>
					<template #cell(company)="row">
						<strong>{{ row.value }}</strong>
						<small v-if="row.item.returningExhibitor" class="text-muted d-block">Returning exhibitor</small>
					</template>
					<template #cell(package)="row">
						<span class="text-capitalize">{{ row.value }}</span>
					</template>
					<template #cell(totalCents)="row">{{ money(row.value) }}</template>
					<template #cell(stripe)="row">
						<a
							v-if="stripeChargeUrl(row.item)"
							:href="stripeChargeUrl(row.item)"
							target="_blank"
							rel="noopener"
							class="text-nowrap"
							@click.stop
						>View in Stripe <i class="fas fa-external-link-alt"></i></a>
					</template>
					<template #cell(createdAtMillis)="row">{{ formatDate(row.value) }}</template>
					<template #cell(status)="row">
						<b-badge :variant="statusVariant(row.value)">{{ row.value }}</b-badge>
						<small v-if="row.item.isTestMode" class="text-warning ml-1">TEST</small>
					</template>
				</b-table>
				<b-pagination
					v-if="exhibitors.length > perPage"
					v-model="exhibitorPage"
					:total-rows="exhibitors.length"
					:per-page="perPage"
					align="center"
					class="my-3"
				/>
			</Card>

			<!-- 6. Order status breakdown -->
			<Card no-body>
				<b-row slot="header" align-v="center">
					<b-col cols="8">
						<h3 class="mb-0">Order Status</h3>
					</b-col>
					<b-col cols="4" class="text-right">
						<BaseButton size="sm" type="primary" @click="exportStatus">
							<i class="fas fa-download"></i> CSV
						</BaseButton>
					</b-col>
				</b-row>
				<b-row class="px-3 pt-4">
					<b-col cols="12" class="mb-4">
						<h5 class="text-muted mb-3">By Status</h5>
						<b-table
							:items="statusData.statuses"
							:fields="statusFields"
							show-empty
							empty-text="No orders in this range."
							small
							responsive
						>
							<template #cell(status)="row">
								<b-badge :variant="statusVariant(row.value)">{{ statusLabel(row.value) }}</b-badge>
							</template>
							<template #cell(totalCents)="row">{{ money(row.value) }}</template>
						</b-table>
					</b-col>
					<b-col cols="12" class="mb-4 pt-4 report-divider">
						<h5 class="text-muted mb-3">Failure Reasons</h5>
						<b-table
							:items="statusData.reasons"
							:fields="reasonFields"
							show-empty
							empty-text="No failed orders in this range."
							small
							responsive
						/>
					</b-col>
				</b-row>
				<div class="mx-3 mb-4 pt-4 report-divider" v-if="statusData.failed.length">
					<h5 class="text-muted mb-3">Failed &amp; Partial Orders</h5>
					<b-table
						:items="statusData.failed"
						:fields="problemFields"
						small
						hover
						responsive
						tbody-tr-class="cursor-pointer"
						@row-clicked="openOrder"
					>
						<template #cell(totalCents)="row">{{ money(row.value) }}</template>
						<template #cell(stripe)="row">
							<a
								v-if="stripeChargeUrl(row.item)"
								:href="stripeChargeUrl(row.item)"
								target="_blank"
								rel="noopener"
								class="text-nowrap"
								@click.stop
							>View in Stripe <i class="fas fa-external-link-alt"></i></a>
						</template>
						<template #cell(createdAtMillis)="row">{{ formatDate(row.value) }}</template>
					</b-table>
				</div>
				<div class="mx-3 mb-4 pt-4 report-divider" v-if="statusData.pending.length">
					<h5 class="text-muted mb-3">Pending (Awaiting Payment)</h5>
					<b-table
						:items="statusData.pending"
						:fields="pendingFields"
						small
						hover
						responsive
						tbody-tr-class="cursor-pointer"
						@row-clicked="openOrder"
					>
						<template #cell(status)="row">
							<b-badge :variant="statusVariant(row.value)">{{ statusLabel(row.value) }}</b-badge>
						</template>
						<template #cell(totalCents)="row">{{ money(row.value) }}</template>
						<template #cell(stripe)="row">
							<a
								v-if="stripeChargeUrl(row.item)"
								:href="stripeChargeUrl(row.item)"
								target="_blank"
								rel="noopener"
								class="text-nowrap"
								@click.stop
							>View in Stripe <i class="fas fa-external-link-alt"></i></a>
						</template>
						<template #cell(createdAtMillis)="row">{{ formatDate(row.value) }}</template>
					</b-table>
				</div>
			</Card>

			<!-- 7. App adoption -->
			<Card no-body>
				<b-row slot="header" align-v="center">
					<b-col cols="8">
						<h3 class="mb-0">App Adoption</h3>
					</b-col>
					<b-col cols="4" class="text-right">
						<BaseButton size="sm" type="primary" @click="exportAdoption">
							<i class="fas fa-download"></i> CSV
						</BaseButton>
					</b-col>
				</b-row>
				<b-table
					:items="adoption"
					:fields="adoptionFields"
					show-empty
					empty-text="No members or pending invites."
					small
					hover
					responsive
					class="mb-0"
				>
					<template #cell(adoptionPct)="row">
						<div class="d-flex align-items-center">
							<span class="mr-2" style="min-width: 44px">{{ row.value }}%</span>
							<b-progress :value="row.value" :max="100" height="6px" class="flex-grow-1" />
						</div>
					</template>
				</b-table>
				<div slot="footer" class="report-footer">
					Signed up = people who created an app account. Pending = invited but not signed up yet.
				</div>
			</Card>
		</div>
	</div>
</template>
<script>
import { collection, getDocs } from '@firebase/firestore';
import ExcelJS from 'exceljs';
import { mapActions, mapGetters } from 'vuex';
import { getOrganizationFirestore } from '../../../resources/organizationFirebase';
import { DashboardChildren } from '../../../routes/routesNames';
import Card from '../../../components/Cards/Card.vue';
import StatsCard from '../../../components/Cards/StatsCard.vue';
import BaseButton from '../../../components/BaseButton.vue';
import BarChart from '../../../components/Charts/BarChart';
import { Charts } from '../../../components/Charts/config';
import DashboardHeader from '../HeaderFooterComponents/DashboardHeader.vue';
import {
	buildDataSetHelper,
	filterOrders,
	summarize,
	productRows,
	overTimeRows,
	couponRows,
	exhibitorRows,
	statusReport,
	adoptionRows,
} from '../../../models/AnalyticsReports';

// Read-only "Advanced Reporting & Analytics" view. Loads the conference
// Firestore collections once, then every report is a computed aggregation
// (models/AnalyticsReports.js) over the in-memory data, so filter changes are
// instant and never re-query. Nothing in this view writes to Firestore.
const COLLECTIONS = ['orders', 'products', 'exhibitorProducts', 'coupons', 'users', 'tempUsers'];

export default {
	name: 'Reports',
	components: { Card, StatsCard, BaseButton, BarChart, DashboardHeader },
	computed: {
		...mapGetters({ organization: 'getOrganization', dataSets: 'getDataSets' }),
		ds() {
			return buildDataSetHelper(this.dataSets);
		},
		dataSetOptions() {
			return [{ value: null, text: 'All events' }].concat(this.ds.options);
		},
		dsId() {
			return this.ds.hasDataSets ? this.dataSetFilter : null;
		},
		filteredOrders() {
			return filterOrders(this.allOrders, {
				fromMs: this.parseDate(this.fromDate, false),
				toMs: this.parseDate(this.toDate, true),
				includeTest: this.includeTest,
			});
		},
		summary() {
			return summarize(this.filteredOrders, this.dsId, this.ds);
		},
		kpis() {
			const s = this.summary;
			return [
				{ title: 'Total collected', value: this.money(s.collectedCents), type: 'gradient-green', icon: 'fas fa-dollar-sign', footer: `Attendee ${this.money(s.attendeeCollectedCents)} · Exhibitor ${this.money(s.exhibitorCollectedCents)}` },
				{ title: 'Net revenue', value: this.money(s.netCents), type: 'gradient-info', icon: 'fas fa-chart-line', footer: 'Gross minus discounts, excluding fees' },
				{ title: 'Gross (list price)', value: this.money(s.grossCents), type: 'gradient-primary', icon: 'fas fa-tags', footer: 'Before coupons and fees' },
				{ title: 'Discounts', value: this.money(s.discountCents), type: 'gradient-orange', icon: 'fas fa-percent', footer: 'Coupon discounts redeemed' },
				{ title: 'Average order', value: this.money(s.paidOrders ? Math.round(s.collectedCents / s.paidOrders) : 0), type: 'gradient-default', icon: 'fas fa-receipt', footer: 'Total collected per paid order' },
				{ title: 'Paid orders', value: String(s.paidOrders), type: 'gradient-primary', icon: 'fas fa-shopping-cart', footer: `${s.attendeeOrders} attendee · ${s.exhibitorOrders} exhibitor` },
				{ title: 'Attendee registrations', value: String(s.attendeeRegistrations), type: 'gradient-green', icon: 'fas fa-user-friends', footer: 'Pass seats purchased' },
				{ title: 'Exhibitor registrations', value: String(s.exhibitorRegistrations), type: 'gradient-red', icon: 'fas fa-briefcase', footer: 'Exhibitor checkouts' },
			];
		},
		products() {
			return productRows(
				this.filteredOrders,
				this.dsId,
				this.ds,
				{ products: this.raw.products, exhibitorProducts: this.raw.exhibitorProducts },
				this.includeZero
			);
		},
		overTime() {
			return overTimeRows(this.filteredOrders, this.dsId, this.ds, this.granularity);
		},
		overTimeTableRows() {
			return this.overTime.slice().reverse();
		},
		chartKey() {
			return `${this.granularity}|${this.dsId}|${this.fromDate}|${this.toDate}|${this.includeTest}`;
		},
		overTimeChartData() {
			return {
				labels: this.overTime.map((r) => r.shortLabel),
				datasets: [
					{
						label: 'Attendee registrations',
						backgroundColor: Charts.colors.theme.success,
						data: this.overTime.map((r) => r.attendeeRegistrations),
						maxBarThickness: 28,
						barPercentage: 0.8,
						categoryPercentage: 0.8,
					},
					{
						label: 'Exhibitor registrations',
						backgroundColor: Charts.colors.theme.primary,
						data: this.overTime.map((r) => r.exhibitorRegistrations),
						maxBarThickness: 28,
						barPercentage: 0.8,
						categoryPercentage: 0.8,
					},
				],
			};
		},
		coupons() {
			return couponRows(this.filteredOrders, this.dsId, this.ds, this.raw.coupons, this.includeZero);
		},
		exhibitors() {
			return exhibitorRows(this.filteredOrders, this.dsId, this.ds);
		},
		statusData() {
			return statusReport(this.filteredOrders, this.dsId, this.ds);
		},
		adoption() {
			return adoptionRows(this.raw.users, this.raw.tempUsers, this.dsId, this.ds);
		},
	},
	data() {
		return {
			isLoading: true,
			loadedAt: null,
			allOrders: [],
			raw: { products: [], exhibitorProducts: [], coupons: [], users: [], tempUsers: [] },
			fromDate: '',
			toDate: '',
			dataSetFilter: null,
			includeTest: false,
			includeZero: false,
			granularity: 'week',
			granularityOptions: [
				{ text: 'Day', value: 'day' },
				{ text: 'Week', value: 'week' },
				{ text: 'Month', value: 'month' },
			],
			perPage: 25,
			exhibitorPage: 1,
			buttonItems: [
				{ index: 1, title: 'Stripe Dashboard', type: 'primary', icon: 'fas fa-external-link-alt' },
				{ index: 0, title: 'Refresh', type: 'primary', icon: 'fas fa-sync-alt' },
			],
			overTimeChartOptions: {
				legend: { display: true, position: 'bottom' },
				tooltips: { mode: 'index', intersect: false },
				scales: {
					xAxes: [
						{
							stacked: true,
							gridLines: { display: false },
							ticks: { maxRotation: 0, minRotation: 0, autoSkip: true, maxTicksLimit: 10, fontSize: 11 },
						},
					],
					yAxes: [{ stacked: true, ticks: { beginAtZero: true, precision: 0 } }],
				},
			},
			overTimeFields: [
				{ key: 'periodLabel', label: 'Period' },
				{ key: 'attendeeRegistrations', label: 'Attendee regs', class: 'text-right' },
				{ key: 'exhibitorRegistrations', label: 'Exhibitor regs', class: 'text-right' },
				{ key: 'orders', label: 'Paid orders', class: 'text-right' },
				{ key: 'collectedCents', label: 'Collected', class: 'text-right' },
				{ key: 'cumulativeRegistrations', label: 'Cumulative regs', class: 'text-right' },
			],
			productFields: [
				{ key: 'type', label: 'Type', sortable: true },
				{ key: 'event', label: 'Event', sortable: true },
				{ key: 'productTitle', label: 'Product', sortable: true },
				{ key: 'productType', label: 'Kind', sortable: true },
				{ key: 'units', label: 'Units', sortable: true, class: 'text-right' },
				{ key: 'orders', label: 'Orders', sortable: true, class: 'text-right' },
				{ key: 'grossCents', label: 'Gross', sortable: true, class: 'text-right' },
			],
			couponFields: [
				{ key: 'code', label: 'Code', sortable: true },
				{ key: 'description', label: 'Description', tdClass: 'report-wrap' },
				{ key: 'value', label: 'Value' },
				{ key: 'couponType', label: 'Flow', sortable: true },
				{ key: 'status', label: 'Status', sortable: true },
				{ key: 'uses', label: 'Uses', sortable: true, class: 'text-right' },
				{ key: 'registrations', label: 'Registrations', sortable: true, class: 'text-right' },
				{ key: 'discountCents', label: 'Total discount', sortable: true, class: 'text-right' },
				{ key: 'collectedCents', label: 'Collected', sortable: true, class: 'text-right' },
			],
			exhibitorFields: [
				{ key: 'company', label: 'Company', sortable: true },
				{ key: 'package', label: 'Package', sortable: true },
				{ key: 'items', label: 'Items', tdClass: 'report-wrap' },
				{ key: 'couponCode', label: 'Coupon' },
				{ key: 'totalCents', label: 'Total', sortable: true, class: 'text-right' },
				{ key: 'createdAtMillis', label: 'Date', sortable: true },
				{ key: 'status', label: 'Status', sortable: true },
				{ key: 'stripe', label: '', class: 'text-right' },
			],
			statusFields: [
				{ key: 'status', label: 'Status' },
				{ key: 'orders', label: 'Orders', class: 'text-right' },
				{ key: 'attendeeOrders', label: 'Attendee', class: 'text-right' },
				{ key: 'exhibitorOrders', label: 'Exhibitor', class: 'text-right' },
				{ key: 'totalCents', label: 'Order value', class: 'text-right' },
			],
			reasonFields: [
				{ key: 'reason', label: 'Reason', tdClass: 'report-wrap' },
				{ key: 'orders', label: 'Orders', class: 'text-right' },
			],
			problemFields: [
				{ key: 'createdAtMillis', label: 'Date', sortable: true },
				{ key: 'type', label: 'Type' },
				{ key: 'buyerName', label: 'Buyer' },
				{ key: 'buyerEmail', label: 'Email' },
				{ key: 'paymentMethodKind', label: 'Method' },
				{ key: 'totalCents', label: 'Amount', class: 'text-right' },
				{ key: 'reason', label: 'Reason' },
				{ key: 'stripe', label: '', class: 'text-right' },
			],
			pendingFields: [
				{ key: 'createdAtMillis', label: 'Date', sortable: true },
				{ key: 'status', label: 'Status' },
				{ key: 'buyerName', label: 'Buyer' },
				{ key: 'buyerEmail', label: 'Email' },
				{ key: 'totalCents', label: 'Amount', class: 'text-right' },
				{ key: 'ageDays', label: 'Age (days)', sortable: true, class: 'text-right' },
				{ key: 'stripe', label: '', class: 'text-right' },
			],
			adoptionFields: [
				{ key: 'event', label: 'Event', sortable: true },
				{ key: 'role', label: 'Role', sortable: true },
				{ key: 'signedUp', label: 'Signed up', sortable: true, class: 'text-right' },
				{ key: 'pending', label: 'Pending invites', sortable: true, class: 'text-right' },
				{ key: 'invited', label: 'Total', sortable: true, class: 'text-right' },
				{ key: 'adoptionPct', label: 'Adoption', sortable: true, thStyle: { minWidth: '160px' } },
				{ key: 'pendingEmailNotSent', label: 'Invite email not sent', sortable: true, class: 'text-right' },
			],
		};
	},
	watch: {
		dsId() {
			this.exhibitorPage = 1;
		},
	},
	methods: {
		...mapActions(['showNotification']),
		money(cents) {
			return Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((Number(cents) || 0) / 100);
		},
		sum(rows, key) {
			return rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
		},
		formatDate(ms) {
			return ms ? new Date(ms).toLocaleString() : '';
		},
		isoDate(ms) {
			return ms ? new Date(ms).toISOString() : '';
		},
		// 'YYYY-MM-DD' from the native date input → local start/end of day.
		parseDate(value, endOfDay) {
			if (!value) return null;
			const parts = value.split('-').map(Number);
			if (parts.length !== 3 || parts.some(isNaN)) return null;
			const d = endOfDay
				? new Date(parts[0], parts[1] - 1, parts[2], 23, 59, 59, 999)
				: new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0);
			return d.getTime();
		},
		statusLabel(status) {
			return { pending_bnpl: 'pending (pay later)' }[status] || status;
		},
		statusVariant(status) {
			switch (status) {
				case 'paid':
					return 'success';
				case 'pending':
				case 'pending_bnpl':
				case 'partial':
					return 'warning';
				case 'failed':
					return 'danger';
				default:
					return 'secondary';
			}
		},
		clearFilters() {
			this.fromDate = '';
			this.toDate = '';
			this.dataSetFilter = null;
			this.includeTest = false;
			this.includeZero = false;
		},
		// Charge IDs are only saved on orders from 2026-10-16 onward; older
		// orders have none, so return '' and render no link.
		stripeChargeUrl(item) {
			if (!item || !item.stripeChargeId) return '';
			const base = item.isTestMode ? 'https://dashboard.stripe.com/test/payments/' : 'https://dashboard.stripe.com/payments/';
			return base + encodeURIComponent(item.stripeChargeId);
		},
		handleHeaderClick(button) {
			if (button.index === 0) this.loadData();
			// The org's staff sign in to their own Stripe account, so the plain
			// dashboard URL is right. Detailed financials live there, not here.
			if (button.index === 1) window.open('https://dashboard.stripe.com/payments', '_blank', 'noopener');
		},
		openOrder(item) {
			if (!item || !item.orderId) return;
			this.$router.push({
				name: DashboardChildren.registrationView.name,
				query: { orderId: item.orderId },
			});
		},
		// Same ExcelJS CSV approach as Registrations.vue's export.
		async downloadCsv(name, columns, rows) {
			const workbook = new ExcelJS.Workbook();
			const sheet = workbook.addWorksheet(name);
			sheet.columns = columns.map((c) => ({ header: c.header, key: c.key, width: c.width || 18 }));
			rows.forEach((r) => sheet.addRow(r));
			const buffer = await workbook.csv.writeBuffer();
			const blob = new Blob([buffer], { type: 'text/csv' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
			const scope = this.dsId ? `-${this.dsId}` : '';
			link.href = url;
			link.download = `report-${slug}${scope}-${Date.now()}.csv`;
			link.click();
			setTimeout(() => URL.revokeObjectURL(url), 0);
		},
		dollars(cents) {
			return ((Number(cents) || 0) / 100).toFixed(2);
		},
		exportOverTime() {
			this.downloadCsv(
				'Registrations over time',
				[
					{ header: 'Period', key: 'periodLabel', width: 24 },
					{ header: 'Period start', key: 'period' },
					{ header: 'Attendee registrations', key: 'attendeeRegistrations' },
					{ header: 'Exhibitor registrations', key: 'exhibitorRegistrations' },
					{ header: 'Paid orders', key: 'orders' },
					{ header: 'Collected (USD)', key: 'collected' },
					{ header: 'Cumulative registrations', key: 'cumulativeRegistrations' },
				],
				this.overTime.map((r) => Object.assign({}, r, { collected: this.dollars(r.collectedCents) }))
			);
		},
		exportProducts() {
			this.downloadCsv(
				'Products',
				[
					{ header: 'Type', key: 'type' },
					{ header: 'Event', key: 'event' },
					{ header: 'Product', key: 'productTitle', width: 40 },
					{ header: 'Product ID', key: 'productId' },
					{ header: 'Kind', key: 'productType' },
					{ header: 'Units', key: 'units' },
					{ header: 'Orders', key: 'orders' },
					{ header: 'Gross (USD)', key: 'gross' },
				],
				this.products.map((r) => Object.assign({}, r, { gross: this.dollars(r.grossCents) }))
			);
		},
		exportCoupons() {
			this.downloadCsv(
				'Coupons',
				[
					{ header: 'Code', key: 'code' },
					{ header: 'Description', key: 'description', width: 50 },
					{ header: 'Value', key: 'value' },
					{ header: 'Flow', key: 'couponType' },
					{ header: 'Status', key: 'status' },
					{ header: 'Uses', key: 'uses' },
					{ header: 'Registrations', key: 'registrations' },
					{ header: 'Total discount (USD)', key: 'discount' },
					{ header: 'Collected (USD)', key: 'collected' },
				],
				this.coupons.map((r) =>
					Object.assign({}, r, { discount: this.dollars(r.discountCents), collected: this.dollars(r.collectedCents) })
				)
			);
		},
		exportExhibitors() {
			this.downloadCsv(
				'Exhibitors',
				[
					{ header: 'Order ID', key: 'orderId' },
					{ header: 'Company', key: 'company', width: 30 },
					{ header: 'Package', key: 'package' },
					{ header: 'Items', key: 'items', width: 60 },
					{ header: 'Contacts', key: 'contacts' },
					{ header: 'Buyer', key: 'buyerName' },
					{ header: 'Buyer email', key: 'buyerEmail', width: 30 },
					{ header: 'Coupon', key: 'couponCode' },
					{ header: 'Subtotal (USD)', key: 'subtotal' },
					{ header: 'Total (USD)', key: 'total' },
					{ header: 'Date', key: 'date' },
					{ header: 'Status', key: 'status' },
					{ header: 'Payment method', key: 'paymentMethodKind' },
					{ header: 'Returning exhibitor', key: 'returning' },
					{ header: 'Test mode', key: 'test' },
					{ header: 'Stripe charge ID', key: 'stripeChargeId' },
				],
				this.exhibitors.map((r) =>
					Object.assign({}, r, {
						subtotal: this.dollars(r.subtotalCents),
						total: this.dollars(r.totalCents),
						date: this.isoDate(r.createdAtMillis),
						returning: r.returningExhibitor ? 'Yes' : '',
						test: r.isTestMode ? 'TEST' : '',
					})
				)
			);
		},
		exportStatus() {
			// One flat sheet: a summary block per status, then each failed /
			// partial / pending order with its reason.
			const rows = [];
			this.statusData.statuses.forEach((s) =>
				rows.push({ section: 'Summary', status: s.status, orders: s.orders, amount: this.dollars(s.totalCents) })
			);
			this.statusData.failed.forEach((f) =>
				rows.push({
					section: 'Failed/partial order', status: f.reason.indexOf('Partial') === 0 ? 'partial' : 'failed',
					orderId: f.orderId, date: this.isoDate(f.createdAtMillis), type: f.type, buyer: f.buyerName,
					email: f.buyerEmail, method: f.paymentMethodKind, amount: this.dollars(f.totalCents), reason: f.reason,
					stripeChargeId: f.stripeChargeId,
				})
			);
			this.statusData.pending.forEach((p) =>
				rows.push({
					section: 'Pending order', status: p.status, orderId: p.orderId, date: this.isoDate(p.createdAtMillis),
					type: p.type, buyer: p.buyerName, email: p.buyerEmail, method: p.paymentMethodKind,
					amount: this.dollars(p.totalCents), reason: p.ageDays !== null ? `${p.ageDays} days old` : '',
					stripeChargeId: p.stripeChargeId,
				})
			);
			this.downloadCsv(
				'Order status',
				[
					{ header: 'Section', key: 'section' },
					{ header: 'Status', key: 'status' },
					{ header: 'Orders', key: 'orders' },
					{ header: 'Order ID', key: 'orderId' },
					{ header: 'Date', key: 'date' },
					{ header: 'Type', key: 'type' },
					{ header: 'Buyer', key: 'buyer' },
					{ header: 'Email', key: 'email', width: 30 },
					{ header: 'Method', key: 'method' },
					{ header: 'Amount (USD)', key: 'amount' },
					{ header: 'Reason', key: 'reason', width: 50 },
					{ header: 'Stripe charge ID', key: 'stripeChargeId' },
				],
				rows
			);
		},
		exportAdoption() {
			this.downloadCsv(
				'App adoption',
				[
					{ header: 'Event', key: 'event', width: 30 },
					{ header: 'Role', key: 'role' },
					{ header: 'Signed up', key: 'signedUp' },
					{ header: 'Pending invites', key: 'pending' },
					{ header: 'Total', key: 'invited' },
					{ header: 'Adoption %', key: 'adoptionPct' },
					{ header: 'Invite email not sent', key: 'pendingEmailNotSent' },
				],
				this.adoption
			);
		},
		loadData() {
			this.isLoading = true;
			const firestore = getOrganizationFirestore(this.organization);
			const failures = [];
			// Each collection loads independently: a collection that is missing
			// or unreadable for this org just yields an empty report section
			// instead of blanking the whole page.
			Promise.all(
				COLLECTIONS.map((name) =>
					getDocs(collection(firestore, name))
						.then((snap) => snap.docs.map((d) => d.data()))
						.catch((error) => {
							failures.push(`${name}: ${error.message}`);
							return [];
						})
				)
			)
				.then(([orders, products, exhibitorProducts, coupons, users, tempUsers]) => {
					this.allOrders = orders;
					this.raw = { products, exhibitorProducts, coupons, users, tempUsers };
					this.loadedAt = new Date();
					if (failures.length) {
						this.showNotification({ type: 'warning', message: `Some report data could not load (${failures.join('; ')})` });
					}
				})
				.finally(() => {
					this.isLoading = false;
				});
		},
	},
	mounted() {
		this.loadData();
	},
};
</script>
<style scoped>
.cursor-pointer {
	cursor: pointer;
}
</style>
<style>
/* Unscoped (b-table renders its own cells), but namespaced to this view. */
.reports-view .report-wrap {
	white-space: normal;
	min-width: 220px;
}
/* Card footnotes: sit in the Card footer slot (b-card-footer supplies the
   divider and header-matching padding); text sized like header subtext. */
.reports-view .report-footer {
	font-size: 0.8125rem;
	color: #8898aa;
	line-height: 1.5;
}
/* Keep the StatsCard icon beside the value instead of wrapping under it. */
.reports-view .report-kpi .card-body > .row {
	flex-wrap: nowrap;
}
.reports-view .report-kpi .card-body > .row > .col {
	min-width: 0;
}
.reports-view .report-kpi .report-kpi-value {
	font-size: 1.75rem;
	line-height: 1.2;
	white-space: nowrap;
}
/* Four KPI cards share a row from 1200px (xl) up. Between 1200 and 1599px
   the cards are narrow, so scale the value with the viewport (sized to fit a
   six-figure value like "$132,190.71" beside the icon). */
@media (min-width: 1200px) and (max-width: 1599.98px) {
	.reports-view .report-kpi .report-kpi-value {
		font-size: clamp(1rem, calc((100vw - 800px) / 26), 1.75rem);
	}
}
.reports-view .report-kpi .icon {
	width: 2.5rem;
	height: 2.5rem;
}
.reports-view .report-kpi .icon i {
	font-size: 1rem;
}
/* Divider between Order Status subsections. */
.reports-view .report-divider {
	border-top: 1px solid #e9ecef;
}
.reports-view .report-kpi .report-kpi-note {
	display: block;
	font-size: 0.75rem;
	line-height: 1.4;
	white-space: normal;
}
</style>
